<template>
  <el-card
    id="pulseRespondents"
    class="clear-card"
    shadow="hover"
    :body-style="{
      padding: 0
    }"
  >
    <div
      slot="header"
      class="header defaultCursor"
    >
      <div class="title">
        Coworkers
      </div>

      <div>
        <el-button
          v-if="user.is_staff || user.is_coach"
          size="small"
          round
          @click="sendReminders"
        >
          Send reminder email
        </el-button>
      </div>
    </div>

    <el-table
      v-if="pulse.pulse1 || pulse.pulse2"
      :key="`respondent_${activatedPulse}`"
      ref="respondentTable"
      :data="formattedRespondents"
      :row-class-name="rowClass"
      empty-text="No Respondents."
      style="width: 100%;"
      class="respondentTable clear-table"
    >
      <el-table-column
        prop="first_name"
        label="Name"
        :min-width="100"
      >
        <template slot-scope="data">
          {{ data.row.first_name }} {{ data.row.last_name }}
        </template>
      </el-table-column>

      <el-table-column
        prop="email"
        label="Email"
        :min-width="170"
      >
        <template slot-scope="data">
          {{ data.row.email }}
        </template>
      </el-table-column>

      <el-table-column
        prop="relationship"
        label="Breakout Group"
        :min-width="100"
      >
        <template slot-scope="data">
          <el-select
            v-if="data.row.relationship !== 'self' && data.row.status"
            :value="data.row.relationship"
            size="small"
            class="statusSelector"
            @change="toggleStatusSelector(data.row, $event)"
          >
            <el-option
              v-for="item in options"
              :key="`Option_${item.label}`"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <span
            v-else
            style="text-transform: capitalize;"
          >
            {{ data.row.relationship }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        prop="status"
        label="Survey Response"
        align="center"
        :min-width="80"
      >
        <template slot-scope="data">
          <i
            v-if="data.row.status"
            class="el-icon-check"
          ></i>
        </template>
      </el-table-column>

      <el-table-column
        prop="pulse_link"
        label="Survey Link"
        align="center"
        :min-width="100"
      >
        <template slot-scope="data">
          <el-button
            class="surveyBtn"
            size="small"
            round
            @click="copyLink(data.row.pulse_link)"
          >
            Copy link
          </el-button>
        </template>
      </el-table-column>

      <el-table-column
        prop="survey_uuid"
        label="Response"
        align="center"
        :min-width="100"
      >
        <template slot-scope="data">
          <el-button
            v-if="data.row.status"
            class="responseBtn"
            size="small"
            type="text"
            round
            @click="toggleViewResponse(data.row.survey_uuid)"
          >
            View response
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <ResponseModal
      :show.sync="responseModal"
      :selected-survey.sync="selectedSurvey"
      :activated-pulse="activatedPulse"
    />

    <StatusChangeModal
      :show.sync="statusModal"
      @changeStatus="changeStatus"
    />
  </el-card>
</template>

<script>
import api from '@/api';
import { mapState } from 'vuex';
import ResponseModal from './modals/ResponseModal.vue';
import StatusChangeModal from './modals/StatusChangeModal.vue';

export default {
  name: 'RespondentTable',

  components: {
    ResponseModal,
    StatusChangeModal,
  },

  props: {
    pulse: {
      type: Object,
      default: null,
    },

    activatedPulse: {
      type: Number,
      default: 1,
    },
  },

  data: () => ({
    options: [
      { value: 'manager', label: 'Manager' },
      { value: 'stakeholder', label: 'Coworker' },
    ],
    responseModal: false,
    statusModal: false,
    selectedSurvey: null,
    statusStack: {
      item: null,
      value: '',
    },
  }),

  computed: {
    ...mapState({
      user: (state) => state.user.user,
    }),

    selectedPulse() {
      return `pulse${this.activatedPulse}`;
    },

    formattedRespondents() {
      // Result basket
      let respondents = [];
      const pulse = this.pulse[this.selectedPulse];
      // if (pulse && Object.keys(pulse).length > 0) {
      if (pulse && pulse.leader) {
        // Leader = Self
        const { leader } = pulse;

        leader.relationship = 'self';
        leader.addLine = true;

        // Stakeholders
        const order = { manager: 1, Coworker: 2, stakeholder: 2 };
        const stakeholders = [...pulse.stakeholders];
        stakeholders
          .sort((a, b) => order[a.relationship] - order[b.relationship])
          .map((el) => {
            const item = el;
            item.addLine = false;
            return item;
          });

        // Create stakeholder line breaker
        const lineMaker = stakeholders.map((el) => el.relationship);
        if (lineMaker.lastIndexOf('manager') > -1) {
          stakeholders[lineMaker.lastIndexOf('manager')].addLine = true;
        }
        if (lineMaker.lastIndexOf('Coworker') > -1) {
          stakeholders[lineMaker.lastIndexOf('Coworker')].addLine = true;
        }
        if (lineMaker.lastIndexOf('stakeholder') > -1) {
          stakeholders[lineMaker.lastIndexOf('stakeholder')].addLine = true;
        }

        respondents = [leader, ...stakeholders];
      }

      return respondents;
    },
  },

  methods: {
    rowClass({ row }) {
      return row.addLine ? 'lineBreak' : '';
    },

    copyLink(link) {
      try {
        this.$copyText(link);
        this.$message.success('Copied');
      } catch (err) {
        console.log(err);
      }
    },

    toggleViewResponse(uuid) {
      this.selectedSurvey = uuid;
      this.responseModal = true;
    },

    toggleStatusSelector(item, event) {
      this.statusStack.item = item;
      this.statusStack.value = event;
      this.statusModal = true;
    },

    async changeStatus() {
      try {
        await api.patchPulseResponse(this.statusStack.item.survey_uuid, {
          relationship: this.statusStack.value,
        });

        const criterion = this.formattedRespondents
          .find((el) => el.survey_uuid === this.statusStack.item.survey_uuid);
        criterion.relationship = this.statusStack.value;

        this.statusModal = false;
      } catch (err) {
        console.log(err);
        this.$message.error('Failed to change relationship.');
      } finally {
        this.statusStack.item = null;
        this.statusStack.value = '';
      }
    },

    async sendReminders() {
      try {
        await api.sendPulseReminder(this.pulse.pulse1.leader.uuid);
        this.$message.success('Reminders sent');
      } catch (err) {
        console.log('err', err);
        if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#pulseRespondents {
  margin-top: 20px;

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      color: var(--themeFontColor);
      font-size: 18px;
      font-weight: bold;
    }

    .el-button {
      background-color: transparent;
      color: var(--themeFontColor);
    }
  }

  .responseBtn {
    color: var(--themeFontColor);
    word-break: break-word;
    white-space: normal;
    text-decoration: underline;
    padding: 0;
  }

  .surveyBtn {
    width: 100%;
    background: transparent;
    color: var(--themeFontColor);
    word-break: break-word;
    white-space: normal;
    padding: 9px 5px;
  }
}
</style>

<style lang="scss">
#pulseRespondents {
  .respondentTable {
    border-radius: 0;
    background-color: transparent;

    .el-table__row.lineBreak:not(:last-child) td {
      border-bottom: 1px solid #c4c4c4;
    }
  }

  .statusSelector {
    .el-input__inner {
      background-color: transparent;
      color: var(--themeFontColor);
      border-color: var(--themeFontColor);
    }
  }
}
</style>
